import { forwardRef } from "react";
import styles from "./Radio.module.css";

export const Radio = forwardRef(
  (
    {
      checked = false,
      onChange,
      name,
      id,
      value,
      label,
      description,
      variant = "standard",
      disabled = false,
      className = "",
      ...props
    },
    ref
  ) => {
    const wrapperClass = [
      styles.radioWrapper,
      variant === "card" ? styles.cardVariant : "",
      checked ? styles.checked : "",
      disabled ? styles.disabled : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <label htmlFor={id || `${name}_${value}`} className={wrapperClass}>
        <input
          ref={ref}
          type="radio"
          id={id || `${name}_${value}`}
          name={name}
          value={value}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className={styles.hiddenInput}
          {...props}
        />

        <span
          className={`${styles.customCircle} ${checked ? styles.checked : ""}`}
          aria-hidden="true"
        >
          {checked && <span className={styles.innerDot} />}
        </span>

        {(label || description) && (
          <div className={styles.textGroup}>
            {label && <span className={styles.label}>{label}</span>}
            {description && (
              <span className={styles.description}>{description}</span>
            )}
          </div>
        )}
      </label>
    );
  }
);

Radio.displayName = "Radio";

export const RadioGroup = ({
  options = [],
  value,
  onChange,
  name,
  direction = "vertical",
  variant = "standard",
  disabled = false,
  className = "",
}) => {
  const handleChange = (optVal) => {
    if (disabled) return;
    if (onChange) {
      onChange({ target: { name, value: optVal } });
    }
  };

  if (variant === "segmented") {
    return (
      <div className={`${styles.segmentedContainer} ${className}`.trim()}>
        {options.map((opt) => {
          const optVal = opt.value !== undefined ? opt.value : opt;
          const optLabel = opt.label || String(opt);
          const isSelected = value === optVal;

          return (
            <div
              key={optVal}
              className={`${styles.segmentedPill} ${
                isSelected ? styles.active : ""
              } ${disabled || opt.disabled ? styles.disabled : ""}`}
              onClick={() => !disabled && !opt.disabled && handleChange(optVal)}
            >
              {opt.icon && <span>{opt.icon}</span>}
              <span>{optLabel}</span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={`${styles.groupContainer} ${
        direction === "horizontal" ? styles.horizontal : styles.vertical
      } ${className}`.trim()}
    >
      {options.map((opt) => {
        const optVal = opt.value !== undefined ? opt.value : opt;
        const optLabel = opt.label || String(opt);
        const isChecked = value === optVal;

        return (
          <Radio
            key={optVal}
            name={name}
            value={optVal}
            label={optLabel}
            description={opt.description}
            checked={isChecked}
            onChange={() => handleChange(optVal)}
            variant={variant}
            disabled={disabled || opt.disabled}
          />
        );
      })}
    </div>
  );
};

export default Radio;
